import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { useI18nStore } from './i18nStore';
import { trackSignUp } from '../utils/fbPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      loading: false,
      error: null,
      _hasHydrated: false,

      setHasHydrated: (state) => set({ _hasHydrated: state }),
      setToken: (token) => set({ token }),
      setUser: (user) => {
        set({ user });
        // Sync language from user's account settings
        if (user?.language) {
          useI18nStore.getState().syncFromUser(user.language);
        }
      },
      
      register: async (email, password, name) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/register`, { email, password, name });
          set({ token: response.data.token, user: response.data.user, loading: false });
          // Sync language from user's account
          if (response.data.user?.language) {
            useI18nStore.getState().syncFromUser(response.data.user.language);
          }
          // Track sign up with Facebook Pixel
          trackSignUp({ 
            email: email,
            content_name: 'NEUROLEX Account'
          });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, loading: false });
          return { success: false, error: message };
        }
      },

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/login`, { email, password });
          set({ token: response.data.token, user: response.data.user, loading: false });
          // Sync language from user's account
          if (response.data.user?.language) {
            useI18nStore.getState().syncFromUser(response.data.user.language);
          }
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          set({ error: message, loading: false });
          return { success: false, error: message };
        }
      },

      loadUser: async () => {
        const { token } = get();
        if (!token) return;
        
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          set({ user: response.data });
          // Sync language from user's account
          if (response.data?.language) {
            useI18nStore.getState().syncFromUser(response.data.language);
          }
          return response.data;
        } catch (error) {
          // Token invalid, clear auth
          set({ token: null, user: null });
          return null;
        }
      },

      completeOnboarding: async (data) => {
        const { token } = get();
        set({ loading: true });
        try {
          const response = await axios.post(`${API}/auth/onboarding`, data, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Update user state with the response data
          set({ user: response.data, loading: false });
          // Sync language from user's account (important: user just chose their language)
          if (response.data?.language) {
            useI18nStore.getState().syncFromUser(response.data.language);
          }
          return { success: true, user: response.data };
        } catch (error) {
          set({ loading: false });
          return { success: false, error: error.response?.data?.detail };
        }
      },

      updateUser: (updates) => {
        set((state) => {
          const newUser = { ...state.user, ...updates };
          // Sync language if it changed
          if (updates.language) {
            useI18nStore.getState().syncFromUser(updates.language);
          }
          return { user: newUser };
        });
      },

      logout: () => {
        set({ token: null, user: null, error: null });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'neurolex-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ token: state.token, user: state.user }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
        // Sync language on rehydration (when app loads from localStorage)
        if (state?.user?.language) {
          useI18nStore.getState().syncFromUser(state.user.language);
        }
      },
    }
  )
);
